import { useState, useEffect } from 'react';
import { getActiveMovements, type Movement } from '../../../api/movements.api';

export default function ActiveVehicles() {
  const [vehicles, setVehicles] = useState<Movement[]>([]);
  const [isLoading, setIsLoading] = useState(true);

  useEffect(() => {
    loadVehicles();
  }, []);

  const loadVehicles = async () => {
    try {
      const active = await getActiveMovements();
      // Ordenar por hora de entrada (más recientes primero) y mostrar máx 5
      const sorted = active
        .sort((a, b) => new Date(b.horaEntrada).getTime() - new Date(a.horaEntrada).getTime())
        .slice(0, 5);
      setVehicles(sorted);
    } catch (error) {
      console.error('Error cargando vehículos activos:', error);
    } finally {
      setIsLoading(false);
    }
  };

  const formatTime = (dateString: string) => {
    return new Date(dateString).toLocaleTimeString('es-ES', { hour: '2-digit', minute: '2-digit' });
  };

  const calculateDuration = (entryTime: string) => {
    const entry = new Date(entryTime);
    const now = new Date();
    const diffMs = now.getTime() - entry.getTime();
    const hours = Math.floor(diffMs / (1000 * 60 * 60));
    const minutes = Math.floor((diffMs % (1000 * 60 * 60)) / (1000 * 60));
    return `${hours}h ${minutes}m`;
  };

  const getVehicleType = (tipo: string) => {
    const types: Record<string, string> = {
      'AUTO': 'Automóvil',
      'MOTO': 'Motocicleta',
      'CAMIONETA': 'Camioneta',
      'CAMION': 'Camión',
      'BICICLETA': 'Bicicleta'
    };
    return types[tipo] || tipo;
  };

  const getVehicleColor = (tipo: string) => {
    const colors: Record<string, string> = {
      'AUTO': 'bg-blue-500',
      'MOTO': 'bg-purple-500',
      'CAMIONETA': 'bg-green-500',
      'CAMION': 'bg-orange-500',
      'BICICLETA': 'bg-teal-500'
    };
    return colors[tipo] || 'bg-gray-500';
  };

  if (isLoading) {
    return (
      <div className="bg-white rounded-2xl p-6 border border-slate-200">
        <div className="animate-pulse">
          <div className="h-6 bg-slate-200 rounded w-1/3 mb-4"></div>
          <div className="space-y-3">
            {[1,2,3,4,5].map(i => (
              <div key={i} className="flex items-center gap-3">
                <div className="w-10 h-10 bg-slate-200 rounded-lg"></div>
                <div className="flex-1">
                  <div className="h-4 bg-slate-200 rounded w-1/2 mb-2"></div>
                  <div className="h-3 bg-slate-100 rounded w-1/3"></div>
                </div>
              </div>
            ))}
          </div>
        </div>
      </div>
    );
  }

  return (
    <div className="bg-white rounded-2xl p-6 border border-slate-200">
      <div className="flex items-center justify-between mb-6">
        <div>
          <h2 className="text-lg font-bold text-slate-800">Vehículos Activos</h2>
          <p className="text-sm text-slate-500 mt-1">Actualmente estacionados</p>
        </div>
        <div className="w-8 h-8 flex items-center justify-center rounded-lg bg-teal-50">
          <span className="text-sm font-bold text-teal-600">{vehicles.length}</span>
        </div>
      </div>

      {vehicles.length === 0 ? (
        <div className="py-8 text-center text-slate-400">
          <i className="ri-parking-box-line text-4xl mb-2"></i>
          <p>No hay vehículos activos</p>
        </div>
      ) : (
        <div className="space-y-3">
          {vehicles.map((movement) => (
            <div
              key={movement.id}
              className="flex items-center gap-3 p-3 rounded-xl hover:bg-slate-50 transition cursor-pointer"
            >
              <div className={`w-10 h-10 ${getVehicleColor(movement.vehiculo.tipo)} rounded-lg flex items-center justify-center flex-shrink-0`}>
                <i className={`${movement.vehiculo.tipo === 'MOTO' || movement.vehiculo.tipo === 'BICICLETA' ? 'ri-motorbike-line' : 'ri-car-line'} text-white`}></i>
              </div>
              <div className="flex-1 min-w-0">
                <p className="font-semibold text-slate-800">{movement.vehiculo.placa}</p>
                <p className="text-xs text-slate-500">{getVehicleType(movement.vehiculo.tipo)}</p>
              </div>
              <div className="text-right">
                <p className="text-sm font-medium text-slate-800">{calculateDuration(movement.horaEntrada)}</p>
                <p className="text-xs text-slate-500">{formatTime(movement.horaEntrada)}</p>
              </div>
            </div>
          ))}
        </div>
      )}
    </div>
  );
}
